import { useEffect, useRef } from 'react'
import { CloseButton } from '../components/CloseButton'
import { getLightspeedApi, isPosHost } from '../lightspeed/pos-api'
import { t } from '../i18n'
import { playSound } from '../sound'
import type { Redemption } from '../flow/machine'

/** Full-screen "Reward Given" celebration (ported from the approved design).
 *
 * The gift pops in and its lid lifts, a pulse expands, confetti bursts, then the localized
 * title and the reward name fade up. CSS animations run on mount (this screen mounts fresh per
 * redemption); the confetti is driven here with the Web Animations API. Tapping anywhere
 * continues: inside the POS it closes the extension (cashier returns to take payment); in a
 * browser it resets to idle via `onContinue`.
 *
 * The title and hint are localized (en/fr) from the WebView locale; the reward NAME comes
 * already localized from the backend.
 */
const CONFETTI_COLORS = ['#ffffff', '#C7CBFB', '#A7ADF9', '#E6E7FE']

export function RedeemedScreen({
  redemption,
  onContinue,
  onClose,
}: {
  redemption: Redemption
  onContinue: () => void
  onClose?: () => void
}) {
  const confettiRef = useRef<HTMLDivElement>(null)

  // Reward cue — this screen mounts fresh per redemption, so a bare mount effect fires once when
  // the celebration appears (i.e. only after a reward was redeemed properly).
  useEffect(() => {
    playSound('reward')
  }, [])

  function handleContinue() {
    if (isPosHost()) {
      getLightspeedApi().close()
      return
    }
    onContinue()
  }

  // Confetti burst — 12 bits fanned out radially, fading as they fall. Mirrors the design's
  // vanilla-JS burst, using the Web Animations API so it plays once on mount.
  useEffect(() => {
    const host = confettiRef.current
    if (!host) return
    const N = 12
    for (let i = 0; i < N; i++) {
      const bit = document.createElement('div')
      const size = 4 + (i % 4) * 2.2
      const round = i % 3 === 0
      bit.className = 'confetti-bit'
      // One write (cssText) instead of several `.style.*` assignments, so the browser lays out once.
      bit.style.cssText = `width:${size}%;background:${CONFETTI_COLORS[i % CONFETTI_COLORS.length]};border-radius:${round ? '50%' : '2px'}`
      host.appendChild(bit)

      const ang = (i / N) * Math.PI * 2 + i * 0.7
      const dist = 70 + (i % 5) * 26
      const dx = Math.cos(ang) * dist
      const dy = Math.sin(ang) * dist

      bit.animate(
        [
          { transform: 'translate(-50%,-50%) scale(0)', opacity: 0, offset: 0 },
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1, offset: 0.18 },
          {
            transform: `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) rotate(${ang * 90}deg)`,
            opacity: 1,
            offset: 0.75,
          },
          {
            transform: `translate(calc(-50% + ${dx * 1.15}px),calc(-50% + ${dy * 1.15 + 22}px)) scale(0.5) rotate(${ang * 140}deg)`,
            opacity: 0,
            offset: 1,
          },
        ],
        { duration: 760, delay: 300, easing: 'cubic-bezier(0.18,0.7,0.3,1)', fill: 'forwards' },
      )
    }
    return () => {
      host.innerHTML = ''
    }
  }, [])

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleContinue}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleContinue()}
      aria-label={`${t('rewardGiven')}: ${redemption.rewardLabel}. ${t('tapToContinue')}`}
      className="reward-stage select-none"
    >
      <CloseButton onClick={onClose} tone="light" />
      <div className="reward-text">
        <p className="reward-title">{t('rewardGiven')}</p>
        <p className="reward-name">{redemption.rewardLabel}</p>
      </div>

      <div className="reward-pulse" />

      <div className="reward-gift">
        <svg viewBox="0 0 200 200" width="100%" aria-hidden="true">
          <rect className="gift-body" x="44" y="96" width="112" height="70" rx="10" fill="#fff" />
          <g className="gift-lid">
            <rect x="36" y="72" width="128" height="26" rx="8" fill="#fff" />
            <path
              d="M101 74 C 95 56, 73 48, 67 61 C 62 71, 84 77, 101 74 Z"
              fill="none"
              stroke="#fff"
              strokeWidth="9"
              strokeLinejoin="round"
            />
            <path
              d="M101 74 C 107 56, 129 48, 135 61 C 140 71, 118 77, 101 74 Z"
              fill="none"
              stroke="#fff"
              strokeWidth="9"
              strokeLinejoin="round"
            />
            <circle cx="101" cy="73" r="5" fill="#fff" />
          </g>
        </svg>
      </div>

      <div ref={confettiRef} className="reward-confetti" />
    </div>
  )
}
